import React, { useState, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { leerArchivo, detectarMapeo, construirFilas, subir, CAMPOS } from '../../servicios/CargaFusion';
import '../cargarconsulta/cargarconsulta.css';
import './cargarfusion.css';

const EXTENSIONES = ['.csv', '.xlsx', '.xls'];

export default function CargarFusion() {
    const [archivo, setArchivo] = useState(null);
    const [mapeo, setMapeo] = useState(null);
    const [filas, setFilas] = useState(null);
    const [previo, setPrevio] = useState({ dupInternos: 0, invalidos: 0, totalArchivo: 0 });
    const [estado, setEstado] = useState('inicial');   // inicial | listo | subiendo | fin
    const [progreso, setProgreso] = useState(null);
    const [arrastrando, setArrastrando] = useState(false);
    const inputRef = useRef(null);

    const procesar = async (file) => {
        if (!file) return;
        const nombre = file.name.toLowerCase();
        if (!EXTENSIONES.some((e) => nombre.endsWith(e))) {
            toast.error(`Formato no válido. Debe ser ${EXTENSIONES.join(', ')}.`); return;
        }
        setArchivo(file); setFilas(null); setMapeo(null); setEstado('inicial');
        try {
            const { headers, filas: crudas } = await leerArchivo(file);
            const map = detectarMapeo(headers);
            if (!map.telefono) {
                toast.error('No se encontró la columna de Teléfono. Renómbrala a "Teléfono" y reintenta.', { autoClose: 8000 });
                setArchivo(null); if (inputRef.current) inputRef.current.value = ''; return;
            }
            const { filas: listas, dupInternos, invalidos } = construirFilas(crudas, map);
            if (listas.length === 0) {
                toast.error('Ningún teléfono quedó válido (deben ser 09XXXXXXXX o 9XXXXXXXX).', { autoClose: 7000 });
                setArchivo(null); return;
            }
            setMapeo(map);
            setFilas(listas);
            setPrevio({ dupInternos, invalidos, totalArchivo: crudas.filter((f) => f && !f.every((c) => String(c).trim() === '')).length });
            setEstado('listo');
            toast.success(`${listas.length.toLocaleString()} números listos para cargar.`);
        } catch (ex) { toast.error(ex.message); setArchivo(null); }
    };

    const onArchivo = (e) => procesar(e.target.files[0]);
    const onDrop = (e) => { e.preventDefault(); setArrastrando(false); if (estado !== 'subiendo') procesar(e.dataTransfer.files?.[0]); };

    const iniciar = async () => {
        if (!filas) return;
        setEstado('subiendo');
        setProgreso({ procesadas: 0, insertadas: 0, existentes: 0, invalidas: 0, totalFilas: filas.length });
        try {
            const res = await subir(filas, setProgreso);
            setProgreso({ ...res, dupInternos: previo.dupInternos, invalidosArchivo: previo.invalidos });
            setEstado('fin'); toast.success('Carga completada');
        } catch (ex) { toast.error('Error: ' + ex.message); setEstado('listo'); }
    };

    const reiniciar = () => {
        setArchivo(null); setFilas(null); setMapeo(null); setEstado('inicial'); setProgreso(null);
        setPrevio({ dupInternos: 0, invalidos: 0, totalArchivo: 0 });
        if (inputRef.current) inputRef.current.value = '';
    };

    const pct = progreso && progreso.totalFilas ? Math.round((progreso.procesadas / progreso.totalFilas) * 100) : 0;

    return (
        <div className="cc-wrap">
            <ToastContainer position="top-right" autoClose={4000} />
            <div className="cc-card">
                <h2 className="cc-title">Cargar base de Fusión</h2>
                <p className="cc-sub">Sube el Excel de Fusión a la <strong>bitácora</strong>. Se detectan solas las columnas útiles (el resto se ignora) y <strong>no se repite ningún número</strong>.</p>

                {estado !== 'fin' && (
                    <div className={`cc-drop ${arrastrando ? 'arrastrando' : ''}`}
                         onDragOver={(e) => { e.preventDefault(); if (estado !== 'subiendo') setArrastrando(true); }}
                         onDragLeave={(e) => { e.preventDefault(); setArrastrando(false); }}
                         onDrop={onDrop}>
                        <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" id="cf-file" className="cc-input" onChange={onArchivo} disabled={estado === 'subiendo'} />
                        <label htmlFor="cf-file" className="cc-label">
                            <i className="bi bi-lightning-charge"></i>
                            <span>{archivo ? archivo.name : 'Arrastra el Excel de Fusión aquí o haz clic'}</span>
                            <small>Excel o CSV — columnas variables</small>
                        </label>
                    </div>
                )}

                {(estado === 'listo' || estado === 'subiendo') && mapeo && (
                    <div className="cf-map">
                        <span className="cf-map__title">Columnas detectadas:</span>
                        <div className="cf-map__grid">
                            {CAMPOS.map((c) => (
                                <div key={c.key} className={`cf-map__item ${mapeo[c.key] ? 'ok' : (c.req ? 'req' : 'off')}`}>
                                    <span className="cf-map__campo">{c.label}{c.req && ' *'}</span>
                                    <span className="cf-map__col">{mapeo[c.key] ? mapeo[c.key].header : '— (vacío)'}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {estado === 'listo' && (
                    <div className="cc-listo">
                        <p>
                            <strong>{filas.length.toLocaleString()}</strong> números únicos listos
                            {previo.dupInternos > 0 && <span className="cc-aviso"> · {previo.dupInternos} duplicado(s) en el archivo omitidos</span>}
                            {previo.invalidos > 0 && <span className="cc-aviso"> · {previo.invalidos} sin teléfono válido</span>}.
                        </p>
                        <button className="cc-btn" onClick={iniciar}>Cargar a la bitácora</button>
                    </div>
                )}

                {estado === 'subiendo' && progreso && (
                    <div className="cc-prog">
                        <div className="cc-bar"><div className="cc-bar__fill" style={{ width: pct + '%' }} /></div>
                        <p>{progreso.procesadas.toLocaleString()} / {progreso.totalFilas.toLocaleString()} ({pct}%)</p>
                    </div>
                )}

                {estado === 'fin' && progreso && (
                    <div className="cc-res">
                        <h3>Resumen de carga</h3>
                        <div className="cc-stat cc-ok"><span>Nuevos cargados</span><strong>{progreso.insertadas.toLocaleString()}</strong></div>
                        <div className="cc-stat cc-upd"><span>Ya existían (omitidos)</span><strong>{progreso.existentes.toLocaleString()}</strong></div>
                        {(progreso.dupInternos > 0) && <div className="cc-stat cc-bad"><span>Dup. en el archivo</span><strong>{progreso.dupInternos.toLocaleString()}</strong></div>}
                        {(progreso.invalidosArchivo > 0) && <div className="cc-stat cc-bad"><span>Sin teléfono válido</span><strong>{progreso.invalidosArchivo.toLocaleString()}</strong></div>}
                        <button className="cc-btn cc-btn--sec" onClick={reiniciar}>Cargar otra base</button>
                    </div>
                )}
            </div>
        </div>
    );
}
